import { Link } from '@warp-ds/components/react/link';
import { Box } from '@warp-ds/react';
import { useState } from 'react';
import { QasaPlatformSelectorButton } from '@/components/button/QasaPlatformSelectorButton';
import { BenefitExpandable } from '@/components/shared/BenefitExpandable';
import { trackCreateOrRepublishAdButton, trackGoToQasaLandingPageFromPlatformSelector } from '@/utils/tracking';

type Props = {
    experiment: number;
    variant: string;
    itemId: string;
};

export const QasaBoxRepublishWithDropdown = ({ itemId, experiment, variant }: Props) => {
    const [loading, setLoading] = useState(false);
    const qasaRepublishUrl = `/travel-api/lotr/qasa/republish/${itemId}?experiment=${experiment}&variant=${variant}`;
    return (
        <Box bordered className="flex flex-col h-full !p-0">
            <div className="flex flex-col flex-1 px-16 pt-16 md:px-24 md:pt-24">
                <div className="mb-4">
                    <img src={'https://images.finncdn.no/dynamic/1280w/lotr/qasa/logo/qasalogo.png'} alt="Qasa" width="178" />
                </div>
                <div className="flex flex-col mt-16">
                    <div className="flex-1">
                        <div className="md:mb-16">
                            <h2 className="t3 mb-8 flex items-center">Lei ut med Qasa</h2>
                            <p className="mb-16 mt-16 md:mt-0 min-h-[48px] md:min-h-0">
                                Den største markedsplassen for utleie av boliger i Norden.
                            </p>
                            <QasaPlatformSelectorButton
                                loading={loading}
                                href={qasaRepublishUrl}
                                onClick={() => {
                                    setLoading(true);
                                    trackCreateOrRepublishAdButton('qasa', itemId, true);
                                    setTimeout(() => {
                                        setLoading(false);
                                    }, 10000);
                                }}
                            />
                            <div className="mb-8 mt-24">
                                <div className="mb-8">Dette får du med Qasa:</div>
                                <BenefitExpandable
                                    itemId={itemId}
                                    title="Annonse som vises både på Qasa og Finn"
                                    items={[
                                        'Leietakere kan finne boligen din både på Qasa og Finn. Dette koster 699 kr når du publiserer annonsen, beløpet vil bli tilbakebetalt når en leieavtale inngås med Qasa.',
                                    ]}
                                />
                                <BenefitExpandable
                                    itemId={itemId}
                                    title="Alltid leie på konto i opptil 6 måneder"
                                    items={[
                                        'Hvis leietakers betaling uteblir eller er forsinket så sørger Qasas partner for at du får leien på konto. Dette inngår i utleiegarantien som dekker ubetalt husleie, skader og tyveri med opptil 250 000 kr.',
                                        <div key={'insurance-rent'} className="mt-8">
                                            Les mer om{' '}
                                            <Link
                                                href="https://support.qasa.com/hc/nb-no/articles/34407216487057-Utleieforsikring"
                                                className="underline"
                                                style={{ color: 'black' }}>
                                                hva utleieforsikringen dekker (support.qasa.com)
                                            </Link>
                                        </div>,
                                    ]}
                                />
                                <BenefitExpandable
                                    itemId={itemId}
                                    title="Slipp stress med depositum"
                                    items={[
                                        'Leietakere som består en grundig bakgrunnssjekk får depositum dekket, slik at de slipper å betale noe på forhånd.',
                                        'For leietakere som må betale på forhånd oppbevares depositumet på en sperret konto gjennom hele leieperioden.',
                                        <div key={'deposit'} className="mt-8">
                                            Les mer om{' '}
                                            <Link
                                                href="https://support.qasa.com/hc/nb-no/articles/34408355144721-Vi-fikser-depositumet"
                                                className="underline"
                                                style={{ color: 'black' }}>
                                                depositum (support.qasa.com)
                                            </Link>
                                        </div>,
                                    ]}
                                />
                                <BenefitExpandable
                                    itemId={itemId}
                                    title="Automatisk fakturering av husleien"
                                    items={[
                                        'Qasas partner tar seg av all fakturering til leietaker så slipper du å bruke tid på det. Du kan også se tidligere og kommende leieinnbetalinger, rapportere hendelser og administrere leieforholdet ditt – alt på ett sted.',
                                        <div key={'rent'} className="mt-8">
                                            Les mer om{' '}
                                            <Link
                                                href="https://support.qasa.com/hc/nb-no/articles/34406983856785-Garantert-husleie"
                                                className="underline"
                                                style={{ color: 'black' }}>
                                                garantert husleie (support.qasa.com)
                                            </Link>
                                        </div>,
                                    ]}
                                />
                                <BenefitExpandable
                                    itemId={itemId}
                                    title="Enkel signering med digital kontrakt"
                                    items={[
                                        'Signer leiekontrakten som den er eller tilpass den etter dine behov. Vår kontrakt er utarbeidet av jurister og følger markedsstandarder for å være trygg for både deg og leietakeren. Kontrakten signeres digitalt med BankID.',
                                    ]}
                                />
                                <BenefitExpandable
                                    itemId={itemId}
                                    title="Ekstra trygghet med utleieforsikring"
                                    items={[
                                        'Din vanlige innboforsikring dekker ikke visse skader som kan oppstå under utleie. Qasas partner dekker ubetalt husleie, skader og tyveri med opptil 250 000 kr.',
                                        <div key={'insurance'} className="mt-8">
                                            Les mer om{' '}
                                            <Link
                                                href="https://support.qasa.com/hc/nb-no/articles/34407216487057-Utleieforsikring"
                                                className="underline"
                                                style={{ color: 'black' }}>
                                                hva utleieforsikringen dekker (support.qasa.com)
                                            </Link>
                                        </div>,
                                    ]}
                                />
                            </div>
                        </div>
                    </div>
                </div>
                <div className="text-16 p-16 rounded-8 min-h-[96px] md:min-h-0" style={{ backgroundColor: '#F6F6F6' }}>
                    <div>Ved signering av Qasa leiekontrakt trekkes 2,49% av månedlige leieinntekter.</div>
                </div>
                <div className="mt-16">
                    <div className="mb-16">Alle bilder og boligdetaljer fra nåværende annonse vil bli overført automatisk.</div>
                    <div className="text-center">
                        <Link
                            href="/realestate/lettings/qasa"
                            className="underline"
                            style={{ color: 'black' }}
                            onClick={() => {
                                trackGoToQasaLandingPageFromPlatformSelector();
                            }}>
                            Les mer om alle fordelene med Qasa
                        </Link>
                    </div>
                </div>
            </div>
            <div className="text-center py-8 mt-16 flex justify-center gap-8 rounded-b-8 min-h-[48]" style={{ backgroundColor: '#F0F0EB' }}>
                <div className="text-s mt-8">En del av</div>
                <img src="/realestate/lettings/icons/qasa/vend.svg" height="24px" width="61px" alt={'Vend logo'} />
            </div>
        </Box>
    );
};
